// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useCallback } from 'react';

import {
  type WorkOrder,
  type WorkOrderItem,
  useEvolvePaginatedGet,
  useEvolvePost,
  type Task,
  type TaskPaginated,
} from '@evolvemep/api-ts';
import { Flex, Stack, Button, Text, Divider } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { TaskAdder } from 'modules/Shop/ShopSetup/WorkCellSetup/ShopSetupPage/TaskTypes/TaskAdder';
import { TaskTypesProvider } from 'modules/Shop/ShopSetup/WorkCellSetup/ShopSetupPage/TaskTypes/useTaskTypes';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import { DeleteTaskModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/DeleteTaskModal';
import { EditableTask } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/EditableTask';

type Props = {
  opened: boolean;
  onClose: () => void;
  workOrder: WorkOrder;
  workOrderItem: WorkOrderItem;
  onTasksUpdated: () => void;
  onTaskAttachmentsChanged?: () => void;
};

const AddTasksToBOPContents = ({
  workOrder,
  workOrderItem,
  onTasksUpdated: onTasksUpdatedParent,
  onTaskAttachmentsChanged,
}: Props) => {
  const {
    data,
    fetchNextPage,
    loading,
    entireCount,
    reset: refresh,
  } = useEvolvePaginatedGet('shop/task', {
    defaultConfig: {
      params: {
        workOrderItemId: workOrderItem.workOrderItemId,
      },
    },
  });

  const { apiCall: addTasksToWorkOrderItems, loading: saving } = useEvolvePost('shop/task');

  const onTasksUpdated = useCallback(() => {
    refresh();
    onTasksUpdatedParent();
  }, [onTasksUpdatedParent, refresh]);

  const [deletingTask, setDeletingTask] = useState<Task | TaskPaginated | null>(null);

  return (
    <Flex flex={1} style={{ overflow: 'hidden' }}>
      <TaskTypesProvider>
        <Stack p="md" w={475} style={{ overflow: 'hidden' }}>
          <TaskAdder
            rightSideComponent={(selectedTasks, selectedGroups, resetSelection) => (
              <Button
                onClick={async () => {
                  await addTasksToWorkOrderItems({
                    taskTypeIds: selectedTasks.map((task) => task.taskTypeId),
                    taskTypeGroupIds: selectedGroups.map((group) => group.taskTypeGroupId),
                    workOrderItemIds: [workOrderItem.workOrderItemId],
                  });
                  resetSelection();
                  onTasksUpdated();
                }}
                disabled={selectedTasks.length === 0 && selectedGroups.length === 0}
                loading={saving}
                leftSection={<EvolveIcon size="sm" icon="Add" />}
              >
                Add to item
              </Button>
            )}
          />
        </Stack>
      </TaskTypesProvider>

      <Divider orientation="vertical" />

      <Stack flex={1} style={{ overflow: 'hidden' }} p="md">
        <Text fw={500}>Item tasks</Text>
        <Stack flex="1 0 auto" style={{ overflowY: 'scroll' }} pr="xs">
          {data.map((task) => (
            <EditableTask
              key={task.taskId}
              task={task}
              workOrder={workOrder}
              onTaskUpdated={(updatedTask) => {
                if (updatedTask.numberOfAttachments !== task.numberOfAttachments) {
                  onTaskAttachmentsChanged?.();
                }
                onTasksUpdated();
              }}
              onDeleteTask={() => setDeletingTask(task)}
              hideDetails
            />
          ))}
          {entireCount === 0 && (
            <Flex justify="center" align="center" flex={1}>
              <Text c="dimmed" fz="sm">
                No tasks found for this item.
              </Text>
            </Flex>
          )}
          <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
        </Stack>
      </Stack>

      <DeleteTaskModal
        task={deletingTask}
        onTaskDeleted={() => {
          onTasksUpdated();
          if ((deletingTask?.numberOfAttachments ?? 0) > 0) {
            onTaskAttachmentsChanged?.();
          }
        }}
        onClose={() => setDeletingTask(null)}
      />
    </Flex>
  );
};

export const AddTasksToBOPModal = (props: Props) => {
  const { workOrder, workOrderItem, opened, onClose } = props;

  return (
    <WrappedModal
      miniHeader={getDisplayNameWO(workOrder)}
      title={workOrderItem.name}
      subtitle="Add tasks to item"
      hideModalButtons
      opened={opened}
      onClose={onClose}
      tall
      size="70rem"
      bodyProps={{ p: 0 }}
    >
      <AddTasksToBOPContents {...props} />
    </WrappedModal>
  );
};
